import { create } from "zustand";
import { API_ENDPOINTS, apiFetchJson } from "@/lib/api";

export interface ServerData {
    id: string;
    name: string;
    game: string;
    status: "online" | "offline" | "maintenance";
    players: { current: number; max: number; known?: boolean; source?: string };
    cpu?: number;
    memory?: number;
    uptime?: string;
    last_check?: string;
    ip?: string;
    port?: number;
}

interface AdminServersStoreState {
    serversArray: ServerData[];
    isLoading: boolean;
    error: string | null;
}

interface AdminServersStoreActions {
    fetchServers: () => Promise<void>;
    refresh: () => Promise<void>;
    startPolling: () => void;
    stopPolling: () => void;

    discoverDocker: () => Promise<any>;
    refreshCache: () => Promise<any>;

    startServer: (serverId: string) => Promise<any>;
    stopServer: (serverId: string) => Promise<any>;
    restartServer: (serverId: string) => Promise<any>;
    getLogs: (serverId: string) => Promise<any>;
}

let _pollRefCount = 0;
let _pollHandle: number | null = null;

export const useAdminServersStore = create<
    AdminServersStoreState & AdminServersStoreActions
>((set, get) => ({
    serversArray: [],
    isLoading: false,
    error: null,

    fetchServers: async () => {
        set({ isLoading: true, error: null });
        try {
            const data = await apiFetchJson<any>(API_ENDPOINTS.SERVERS);
            const rawList: any[] = Array.isArray(data)
                ? data
                : Array.isArray(data?.servers)
                  ? data.servers
                  : [];
            const fetchedAt = new Date().toISOString();
            const list: ServerData[] = rawList.map((server) => ({
                ...server,
                last_check:
                    typeof server?.last_check === "string" &&
                    server.last_check.trim()
                        ? server.last_check
                        : typeof server?.lastUpdate === "string" &&
                            server.lastUpdate.trim()
                          ? server.lastUpdate
                          : fetchedAt,
            }));
            set({ serversArray: list, isLoading: false });
        } catch (err) {
            const msg =
                err instanceof Error ? err.message : "Failed to fetch servers";
            console.error("Error fetching servers:", err);
            set({ serversArray: [], isLoading: false, error: msg });
        }
    },

    refresh: async () => get().fetchServers(),

    startPolling: () => {
        _pollRefCount += 1;
        if (_pollRefCount !== 1) return;

        void get().fetchServers();
        if (typeof window === "undefined") return;

        _pollHandle = window.setInterval(() => {
            void get().fetchServers();
        }, 30000);
    },

    stopPolling: () => {
        _pollRefCount = Math.max(0, _pollRefCount - 1);
        if (_pollRefCount !== 0) return;
        if (_pollHandle === null) return;

        window.clearInterval(_pollHandle);
        _pollHandle = null;
    },

    discoverDocker: async () => {
        return apiFetchJson<any>(API_ENDPOINTS.SERVERS_DOCKER_DISCOVERY);
    },

    refreshCache: async () => {
        const res = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_CACHE_REFRESH, {
            method: "POST",
        });
        await get().fetchServers();
        return res;
    },

    startServer: async (serverId: string) => {
        const res = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_ADMIN_ACTION, {
            method: "POST",
            query: { action: "start", server_id: serverId },
        });
        await get().fetchServers();
        return res;
    },
    stopServer: async (serverId: string) => {
        const res = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_ADMIN_ACTION, {
            method: "POST",
            query: { action: "stop", server_id: serverId },
        });
        await get().fetchServers();
        return res;
    },
    restartServer: async (serverId: string) => {
        const res = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_ADMIN_ACTION, {
            method: "POST",
            query: { action: "restart", server_id: serverId },
        });
        await get().fetchServers();
        return res;
    },
    getLogs: async (serverId: string) => {
        const res = await apiFetchJson<any>(API_ENDPOINTS.SERVERS_ADMIN_ACTION, {
            method: "POST",
            query: { action: "logs", server_id: serverId },
        });
        await get().fetchServers();
        return res;
    },
}));
